import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { DesiredAction } from '@dreamai/types';
import { getActionKeyLabel } from '../../../lib/desiredActionFields';
import { ACTION_ICONS } from './ResearchSuggestions';
import type { CompletedAction } from './ProjectAppBlockRenderer';
import { ReactComponent as PenIcon } from '../../../icons/edit.svg';

/**
 * Strip orphaned CSS that was left behind when <style> tags were stripped.
 * Some email processors remove <style> tags but leave the CSS text content,
 * which then renders as visible text in the iframe.
 */
function sanitizeOrphanedCss(html: string): string {
  // 1. Protect genuine <style> blocks so we don't touch them
  const styleBlocks: string[] = [];
  let cleaned = html.replace(/<style[\s>][\s\S]*?<\/style>/gi, (match) => {
    styleBlocks.push(match);
    return `<!--STYLE_PRESERVED_${styleBlocks.length - 1}-->`;
  });

  // 2. Remove orphaned @-rules (font-face, media, import, keyframes)
  //    Handles up to 2 levels of nested braces: @media { @font-face { ... } }
  cleaned = cleaned.replace(
    /(?:\d+\s+)?@(?:font-face|media|import|keyframes)\b[^{]*\{(?:[^{}]*\{[^{}]*\})*[^{}]*\}/gi,
    '',
  );

  // 3. Remove bare CSS selector rules (e.g. "td, span, a { font-family: ...; }")
  //    Only matches when selectors are common HTML element names
  cleaned = cleaned.replace(
    /(?:\d+\s+)?(?:(?:td|tr|th|span|div|a|p|body|html|img|table|ul|ol|li|h[1-6])\s*,?\s*)+\{[^{}]*\}/gi,
    '',
  );

  // 4. Restore preserved <style> blocks
  cleaned = cleaned.replace(
    /<!--STYLE_PRESERVED_(\d+)-->/g,
    (_, i) => styleBlocks[+i],
  );

  return cleaned;
}

interface ProjectAppEmailContentBlockProps {
  blockId: string;
  data: Record<string, unknown>;
  /** When provided, desired actions are rendered as interactive buttons */
  onCardActionClick?: (action: DesiredAction, blockId: string) => void;
  completedActions?: CompletedAction[];
  agentName?: string;
  /** Hide the "Open in Gmail" link (e.g. on public shared pages) */
  hideGmailLink?: boolean;
}

const ProjectAppEmailContentBlock: React.FC<
  ProjectAppEmailContentBlockProps
> = ({
  blockId,
  data,
  onCardActionClick,
  completedActions = [],
  agentName,
  hideGmailLink,
}) => {
  const {
    subject,
    from: emailFrom,
    to: emailTo,
    date: emailDate,
    html: emailHtml,
    gmailMessageId,
    accountEmail,
    desiredActions: emailActions,
  } = data as {
    subject?: string;
    from?: string;
    to?: string;
    date?: string;
    html?: string;
    gmailMessageId?: string;
    accountEmail?: string;
    desiredActions?: DesiredAction[];
  };

  const gmailUrl = gmailMessageId
    ? accountEmail
      ? `https://mail.google.com/mail/?authuser=${encodeURIComponent(accountEmail)}&ogbl#all/${gmailMessageId}`
      : `https://mail.google.com/mail/u/0/#all/${gmailMessageId}`
    : null;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeHeight, setIframeHeight] = useState(0);

  // Detect whether the raw HTML already carries meaningful styling
  // Only <style> blocks or <table> layouts count — plain reply chains with
  // a few inline style= attributes still need default padding.
  const hasOwnStyling = useMemo(() => {
    if (!emailHtml) return false;
    return /<style[\s>]/i.test(emailHtml) || /<table[\s>]/i.test(emailHtml);
  }, [emailHtml]);

  // Wrap the raw HTML in a minimal document with conditional defaults
  const wrappedHtml = useMemo(() => {
    if (!emailHtml) return '';
    const cleanedHtml = sanitizeOrphanedCss(emailHtml);
    const padding = hasOwnStyling ? '0' : '12px 16px';
    return `<!DOCTYPE html>
<html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  body, html {
    font-family: var(--dream-font-body);
    font-size: 14px;
    line-height: 1.5;
    margin: 0;
    padding: ${padding};
    color: #1a1a1a;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
  a { color: #0066cc; text-decoration: underline; }
</style>
</head><body>${cleanedHtml}</body></html>`;
  }, [emailHtml, hasOwnStyling]);

  // Auto-size iframe to its content height
  const adjustHeight = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        const h = doc.documentElement.scrollHeight;
        setIframeHeight(h);
      }
    } catch {
      // cross-origin guard
    }
  }, []);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    iframe.addEventListener('load', adjustHeight);
    const t = setTimeout(adjustHeight, 300);
    return () => {
      iframe.removeEventListener('load', adjustHeight);
      clearTimeout(t);
    };
  }, [wrappedHtml, adjustHeight]);

  return (
    <div className='project-app-block-email-content'>
      <div className='project-app-block-email-content__header'>
        <div className='project-app-block-email-content__subject-row'>
          {subject && (
            <h3 className='project-app-block-email-content__subject'>
              {subject}
            </h3>
          )}
          {gmailUrl && !hideGmailLink && (
            <a
              className='project-app-block-email-content__gmail-pill'
              href={gmailUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              Open in Gmail
              <span className='project-app-block-email-content__gmail-arrow'>
                {'\u2197'}
              </span>
            </a>
          )}
        </div>
        <div className='project-app-block-email-content__meta'>
          {emailFrom && (
            <span className='project-app-block-email-content__from'>
              From: {emailFrom}
            </span>
          )}
          {emailTo && (
            <span className='project-app-block-email-content__to'>
              To: {emailTo}
            </span>
          )}
          {emailDate && (
            <span className='project-app-block-email-content__date'>
              {emailDate}
            </span>
          )}
        </div>
      </div>
      {emailHtml && (
        <iframe
          ref={iframeRef}
          className='project-app-block-email-content__iframe'
          srcDoc={wrappedHtml}
          sandbox='allow-same-origin'
          title={subject || 'Email content'}
          style={iframeHeight ? { height: iframeHeight } : undefined}
        />
      )}
      {onCardActionClick &&
        emailActions &&
        emailActions.length > 0 &&
        (() => {
          // Split actions into draft-email pills vs other actions
          const draftActions = emailActions.filter(
            (a) => a.key === 'draft_email_from_user',
          );
          const otherActions = emailActions.filter(
            (a) => a.key !== 'draft_email_from_user',
          );
          // All drafts share the same key, so one completion hides them all
          const draftCompleted = completedActions.find(
            (c) => c.id === `block:${blockId}:draft_email_from_user`,
          );

          return (
            <>
              {draftActions.length > 0 && (
                <div className='email-content-draft-section'>
                  {draftCompleted ? (
                    <div
                      className={`email-content-draft-section__completed ${draftCompleted.phase}`}
                    >
                      {draftCompleted.phase === 'created' ? (
                        <span className='email-content-draft-section__tick'>
                          {'\u2713'} Todo Created
                        </span>
                      ) : (
                        <a
                          className='email-content-draft-section__view-link'
                          href='/'
                          onClick={(e) => {
                            e.preventDefault();
                            window.location.href = '/';
                          }}
                        >
                          View in Todo List {'\u2192'}
                        </a>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className='email-content-draft-section__header'>
                        <PenIcon className='email-content-draft-section__icon' />
                        <div className='email-content-draft-section__title'>
                          <span className='email-content-draft-section__name'>
                            Draft Email from You
                          </span>
                          <span className='email-content-draft-section__subtitle'>
                            I will draft a reply
                          </span>
                        </div>
                      </div>
                      <div className='email-content-draft-section__pills'>
                        {draftActions.map((action, i) => {
                          // Strip the common "I will draft a reply/an email" stem from the label
                          const rawLabel =
                            action.label || action.todoText || 'Draft a reply';
                          const pillLabel = rawLabel
                            .replace(
                              /^I will draft (a reply|an email|a response)\s*/i,
                              '',
                            )
                            .replace(/^I will\s*/i, '');
                          return (
                            <button
                              key={`draft-${i}`}
                              className='email-content-draft-section__pill'
                              onClick={() => onCardActionClick(action, blockId)}
                            >
                              {pillLabel}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              )}
              {otherActions.length > 0 && (
                <div className='project-app-block-card__actions'>
                  {otherActions.map((action, i) => {
                    const Icon = ACTION_ICONS[action.key];
                    const actionLabel = getActionKeyLabel(
                      action.key,
                      agentName,
                    );
                    const completed = completedActions.find(
                      (c) => c.id === `block:${blockId}:${action.key}`,
                    );
                    if (completed) {
                      return (
                        <div
                          key={`${action.key}-${i}`}
                          className={`project-app-block-card__action-btn completed ${completed.phase}`}
                        >
                          {completed.phase === 'created' ? (
                            <span className='project-app-block-card__action-tick'>
                              {'\u2713'} Todo Created
                            </span>
                          ) : (
                            <a
                              className='project-app-block-card__action-view-link'
                              href='/'
                              onClick={(e) => {
                                e.preventDefault();
                                window.location.href = '/';
                              }}
                            >
                              View in Todo List {'\u2192'}
                            </a>
                          )}
                        </div>
                      );
                    }
                    return (
                      <button
                        key={`${action.key}-${i}`}
                        className='project-app-block-card__action-btn'
                        onClick={() => onCardActionClick(action, blockId)}
                      >
                        {Icon && (
                          <Icon className='project-app-block-card__action-icon' />
                        )}
                        <div className='project-app-block-card__action-text'>
                          <span className='project-app-block-card__action-key'>
                            {actionLabel}
                          </span>
                          {action.label && (
                            <span className='project-app-block-card__action-label'>
                              {action.label}
                            </span>
                          )}
                        </div>
                        <span className='project-app-block-card__action-arrow'>
                          {'\u2192'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          );
        })()}
    </div>
  );
};

export default ProjectAppEmailContentBlock;
